import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons';

import Header from '../Header/Header';
import Grid from '../../UI/HOC/Grid/Grid';
import Button from '../../UI/Button/Button';
import Content from '../../UI/HOC/Content/Content';
import Section from '../../UI/HOC/Section/Section';
import ContactPage from '../Homepage/Contact.page/Contact.page';
import { bookingUrlWithMarketingParams } from '../../utils/bookingUrlWithMarketingParams';
import {
	resolveWorkshop,
	mainSiteBasePath,
} from '../../utils/resolveWorkshop';

import css from './GoogleAdsLanding.module.css';

function defaultGoogleAds(workshop) {
	return {
		metaTitle: `${workshop.name} — Book online`,
		metaDescription: workshop.intro,
		eyebrow: 'Book online',
		headline: `Book your next visit at ${workshop.name}`,
		subheadline: workshop.intro,
		bullets: [
			'Easy online booking',
			'Trusted local workshop',
			'Quality service and transparent communication',
		],
		primaryCta: 'Book your service',
	};
}

const GoogleAdsLanding = () => {
	const { id } = useParams();
	const host = window.location.hostname;
	const workshop = resolveWorkshop(host, id);

	if (!workshop) {
		return (
			<div className={css.NotFound}>
				<p>We could not find this workshop. Check the link or try again.</p>
			</div>
		);
	}

	const ads = workshop.googleAds || defaultGoogleAds(workshop);
	const siteBase = mainSiteBasePath(host, id);

	document.documentElement.style.setProperty('--main-color', workshop.theme);

	const goBook = () => {
		window.location.href = bookingUrlWithMarketingParams(workshop.bookingLink);
	};

	return (
		<div className={css.Page}>
			<Helmet>
				<title>{ads.metaTitle}</title>
				<meta name="description" content={ads.metaDescription} />
			</Helmet>
			<Header logo={workshop.logo} bookingLink={workshop.bookingLink} />
			<div className={css.SiteNavBar}>
				<Content>
					<nav className={css.SiteNav} aria-label="Main site pages">
						<Link className={css.SiteNavLink} to={siteBase}>
							Home
						</Link>
						<Link className={css.SiteNavLink} to={`${siteBase}#services`}>
							Services
						</Link>
						<Link className={css.SiteNavLink} to={`${siteBase}#about`}>
							About
						</Link>
					</nav>
				</Content>
			</div>
			<Section className={css.HeroSection}>
				<Content>
					<Grid column={2} className={css.Grid} isGap={true}>
						<div className={css.CopyCol}>
							<p className={css.Eyebrow}>{ads.eyebrow}</p>
							<h1 className={css.Headline}>{ads.headline}</h1>
							<p className={css.Subhead}>{ads.subheadline}</p>
							<div className={css.CtaRow}>
								<Button
									onClick={goBook}
									color="main"
									className={css.HeroCta}
								>
									{ads.primaryCta}
								</Button>
							</div>
							<ul className={css.Bullets} aria-label="Why book with us">
								{ads.bullets.map((text, i) => (
									<li key={i}>
										<FontAwesomeIcon
											icon={faCheckCircle}
											className={css.BulletIcon}
											aria-hidden="true"
										/>
										<span>{text}</span>
									</li>
								))}
							</ul>
						</div>
						<div className={css.ImageCol}>
							<img
								className={css.HeroImage}
								src={workshop.heroImage}
								alt={`${workshop.name}`}
							/>
						</div>
					</Grid>
				</Content>
			</Section>
			<ContactPage
				mapSize="small"
				workshopName={workshop.name}
				contacts={workshop.contacts}
				extraLogos={workshop.extraLogos ?? []}
			/>
		</div>
	);
};

export default GoogleAdsLanding;
